import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { useOktaSession } from "../okta/OktaSessionContext.jsx";
import { storeToken } from "../services/tokenVault.js";
import Button from "./ui/Button.jsx";
import FRAMEWORKS from "../compliance/frameworks.js";
import { formatComplianceScopeDisplay } from "../compliance/complianceScopeDisplay.js";
import ConnectEntraPanel from "./onboarding/ConnectEntraPanel.jsx";
import ConnectAuth0Panel from "./onboarding/ConnectAuth0Panel.jsx";
import ConnectOryPanel from "./onboarding/ConnectOryPanel.jsx";
import ConnectOrySelfHostedPanel from "./onboarding/ConnectOrySelfHostedPanel.jsx";
import { ORY_SELF_HOSTED_INTRO } from "./onboarding/orySelfHostedConnectGuidance.js";

const NEW_PROJECT_STEP_HEADERS = {
  choose: {
    title: "Choose your identity provider",
    subtitle: "Select the IdP you want to assess. idwiz runs a read-only org audit after connect.",
  },
  okta: {
    title: "Connect an Okta org",
    subtitle: "I'll analyze your org instantly once connected.",
  },
  entra: {
    title: "Connect Microsoft Entra",
    subtitle: "I'll analyze your org instantly once connected.",
  },
  auth0: {
    title: "Connect Auth0",
    subtitle: "I'll analyze your org instantly once connected.",
  },
  ory: {
    title: "Connect Ory Network",
    subtitle: "I'll analyze your org instantly once connected.",
  },
  "ory-self-hosted": {
    title: "Connect a self-hosted Ory deployment",
    subtitle: ORY_SELF_HOSTED_INTRO,
  },
};

function deriveOrgName(url, fallback) {
  if (fallback?.trim()) return fallback.trim();
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.split(".")[0] || "Project";
  } catch {
    return "Project";
  }
}

export default function NewProjectModal({ onClose, onCreated }) {
  const navigate = useNavigate();
  const { validateSswsToken } = useAuth();
  const { setSession } = useOktaSession();
  const [step, setStep] = useState("choose");
  const [orgUrl, setOrgUrl] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [projectName, setProjectName] = useState("");
  const [environment, setEnvironment] = useState("production");
  const [frameworks, setFrameworks] = useState(["OKTA-BASELINE"]);
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleBackdropPointerDown = () => {
    setShake(true);
    window.setTimeout(() => setShake(false), 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const ou = orgUrl.trim();
    const tok = apiToken.trim();
    if (!ou || !tok) {
      setError("Org URL and API token are required.");
      return;
    }
    setLoading(true);
    try {
      await validateSswsToken(ou, tok);
      storeToken(ou, tok);
      const orgName = deriveOrgName(ou, projectName);
      const res = await fetch("/api/projects", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orgUrl: ou,
          orgName,
          environment:
            environment === "production"
              ? "Production"
              : environment === "sandbox"
                ? "Preview / Sandbox"
                : "Development",
          complianceFrameworks: normalizeFrameworksForSubmit(frameworks),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.project?.projectId) {
        throw new Error(data.error || "Could not create project");
      }
      const { projectId } = data.project;
      try {
        sessionStorage.setItem("idwiz.activeProjectId", projectId);
        sessionStorage.setItem("idwiz.orgUrl", ou);
      } catch {
        /* ignore */
      }
      setSession({ orgUrl: ou, apiToken: tok });
      onCreated?.();
      navigate(`/project/${encodeURIComponent(projectId)}/connect`, {
        state: { orgUrl: ou, projectId },
      });
    } catch (err) {
      setError(err?.message || "Connection failed");
    } finally {
      setLoading(false);
    }
  };

  const stepHeader = NEW_PROJECT_STEP_HEADERS[step] || NEW_PROJECT_STEP_HEADERS.okta;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "#00000088",
        display: "grid",
        placeItems: "center",
        padding: "24px",
        cursor: "default",
      }}
      role="presentation"
      onPointerDown={handleBackdropPointerDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={shake ? "shake" : undefined}
        style={{
          width: "100%",
          maxWidth: "480px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "var(--bg-surface)",
          border: "1px solid var(--border-bright)",
          borderRadius: "8px",
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div style={{ flexShrink: 0, padding: "24px 32px 0" }}>
          <h2 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "8px" }}>
            {stepHeader.title}
          </h2>
          <p className="text-secondary" style={{ fontSize: "14px", margin: 0 }}>
            {stepHeader.subtitle}
          </p>
        </div>

        {step === "choose" ? (
          <div style={{ padding: "24px 32px 32px", display: "grid", gap: "12px" }}>
            <button
              type="button"
              onClick={() => setStep("okta")}
              style={providerCardStyle}
            >
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Okta</span>
              <span className="text-dim" style={{ fontSize: "12px", marginTop: "4px" }}>
                SSWS API token — session stored in your browser
              </span>
            </button>
            <button
              type="button"
              onClick={() => setStep("entra")}
              style={providerCardStyle}
            >
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Microsoft Entra ID</span>
              <span className="text-dim" style={{ fontSize: "12px", marginTop: "4px" }}>
                App registration — client credentials stored encrypted on the server
              </span>
            </button>
            <button
              type="button"
              onClick={() => setStep("auth0")}
              style={providerCardStyle}
            >
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Auth0</span>
              <span className="text-dim" style={{ fontSize: "12px", marginTop: "4px" }}>
                M2M credentials — stored encrypted on the server (Management API read-only)
              </span>
            </button>
            <button
              type="button"
              onClick={() => setStep("ory")}
              style={providerCardStyle}
            >
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Ory Network</span>
              <span className="text-dim" style={{ fontSize: "12px", marginTop: "4px" }}>
                Project API key — stored encrypted on the server (Kratos/Hydra admin read-only)
              </span>
            </button>
            <button
              type="button"
              onClick={() => setStep("ory-self-hosted")}
              style={providerCardStyle}
            >
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Ory (self-hosted)</span>
              <span className="text-dim" style={{ fontSize: "12px", marginTop: "4px" }}>
                External Kratos/Hydra admin URLs — tier-agnostic, read-only assessment
              </span>
            </button>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}

        {step === "entra" ? (
          <ConnectEntraPanel
            onBack={() => setStep("choose")}
            onConnected={(projectId) => {
              onCreated?.();
              navigate(`/project/${encodeURIComponent(projectId)}/connect`);
            }}
          />
        ) : null}

        {step === "auth0" ? (
          <ConnectAuth0Panel
            onBack={() => setStep("choose")}
            onConnected={(projectId) => {
              onCreated?.();
              navigate(`/project/${encodeURIComponent(projectId)}/connect`);
            }}
          />
        ) : null}

        {step === "ory-self-hosted" ? (
          <ConnectOrySelfHostedPanel
            onBack={() => setStep("choose")}
            onConnected={(projectId) => {
              onCreated?.();
              navigate(`/project/${encodeURIComponent(projectId)}/connect`);
            }}
          />
        ) : null}

        {step === "ory" ? (
          <ConnectOryPanel
            onBack={() => setStep("choose")}
            onConnected={(projectId) => {
              onCreated?.();
              navigate(`/project/${encodeURIComponent(projectId)}/connect`);
            }}
          />
        ) : null}

        {step === "okta" ? (
        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: "20px 32px 8px",
            }}
          >
          <label style={{ display: "block", marginBottom: "16px" }}>
            <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
              Okta org URL
            </span>
            <input
              required
              type="text"
              value={orgUrl}
              onChange={(e) => setOrgUrl(e.target.value)}
              placeholder="https://yourcompany.okta.com"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "4px",
                border: "1px solid var(--border)",
                background: "var(--bg-input)",
                color: "var(--text-primary)",
                fontSize: "14px",
              }}
            />
            <span className="text-dim" style={{ fontSize: "11px", display: "block", marginTop: "4px" }}>
              Your Okta domain — find it in the Okta Admin console URL
            </span>
          </label>

          <label style={{ display: "block", marginBottom: "16px" }}>
            <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
              API token
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                required
                type={showToken ? "text" : "password"}
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                placeholder="00abc..."
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: "4px",
                  border: "1px solid var(--border)",
                  background: "var(--bg-input)",
                  color: "var(--text-primary)",
                  fontSize: "14px",
                }}
              />
              <button
                type="button"
                className="text-secondary"
                style={{
                  background: "var(--bg-elevated)",
                  border: "1px solid var(--border)",
                  borderRadius: "4px",
                  padding: "0 12px",
                  cursor: "pointer",
                  color: "var(--text-secondary)",
                }}
                onClick={() => setShowToken((s) => !s)}
              >
                {showToken ? "Hide" : "Show"}
              </button>
            </div>
            <span className="text-dim" style={{ fontSize: "11px", display: "block", marginTop: "4px" }}>
              Create in Okta Admin → Security → API → Tokens. We never store this on the server.
            </span>
          </label>

          <label style={{ display: "block", marginBottom: "16px" }}>
            <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
              Project name (optional)
            </span>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Acme Corp — Production"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "4px",
                border: "1px solid var(--border)",
                background: "var(--bg-input)",
                color: "var(--text-primary)",
                fontSize: "14px",
              }}
            />
            <span className="text-dim" style={{ fontSize: "11px", display: "block", marginTop: "4px" }}>
              Defaults to your org name if left blank
            </span>
          </label>

          <label style={{ display: "block", marginBottom: "24px" }}>
            <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
              Environment
            </span>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "4px",
                border: "1px solid var(--border)",
                background: "var(--bg-input)",
                color: "var(--text-primary)",
                fontSize: "14px",
              }}
            >
              <option value="production">Production</option>
              <option value="sandbox">Preview / Sandbox</option>
              <option value="development">Development</option>
            </select>
          </label>

          <div style={{ marginBottom: "24px" }}>
            <div
              className="text-secondary"
              style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}
            >
              Compliance frameworks
            </div>
            <div className="text-dim" style={{ fontSize: "11px", marginBottom: "10px" }}>
              Select the standards your org is held to. This changes which findings are prioritized.
            </div>

            <FrameworkPicker value={frameworks} onChange={setFrameworks} />
          </div>

            {error ? (
              <p className="text-critical" style={{ fontSize: "13px", marginBottom: "12px" }}>
                {error}
              </p>
            ) : null}
          </div>

          <div
            style={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              padding: "16px 32px 24px",
              borderTop: "1px solid var(--border)",
              boxShadow: "0 -8px 24px rgba(0, 0, 0, 0.12)",
            }}
          >
            <Button type="button" variant="ghost" onClick={() => setStep("choose")} disabled={loading}>
              Back
            </Button>
            <Button type="submit" variant="primary" loading={loading} disabled={loading}>
              {loading ? "Connecting…" : "Connect & Analyze"}
            </Button>
          </div>
        </form>
        ) : null}
      </div>
    </div>
  );
}

const providerCardStyle = {
  textAlign: "left",
  padding: "16px",
  borderRadius: "8px",
  border: "1px solid var(--border-bright)",
  background: "var(--bg-elevated)",
  cursor: "pointer",
  display: "block",
  width: "100%",
};

function normalizeFrameworksForSubmit(list) {
  const arr = Array.isArray(list) ? list.map((x) => String(x)).filter(Boolean) : [];
  const uniq = [...new Set(arr)];
  if (!uniq.length) return ["OKTA-BASELINE"];
  // If any specific framework is selected, auto-drop OKTA-BASELINE.
  const hasSpecific = uniq.some((x) => x !== "OKTA-BASELINE");
  if (hasSpecific) return uniq.filter((x) => x !== "OKTA-BASELINE");
  return ["OKTA-BASELINE"];
}

function FrameworkPicker({ value, onChange }) {
  const v = Array.isArray(value) ? value : [];
  const set = new Set(v);

  const optionIds = ["NIST-800-63B", "SOC2", "HIPAA", "PCI-DSS-v4"];
  const items = optionIds.map((id) => ({
    id,
    label: FRAMEWORKS[id]?.displayName || FRAMEWORKS[id]?.name || id,
  }));

  const toggle = (id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const hasSpecific = [...next].some((x) => x !== "OKTA-BASELINE");
    if (hasSpecific) next.delete("OKTA-BASELINE");
    onChange?.([...next]);
  };

  const toggleBaseline = () => {
    const next = new Set(set);
    if (next.has("OKTA-BASELINE")) next.delete("OKTA-BASELINE");
    else {
      next.clear();
      next.add("OKTA-BASELINE");
    }
    onChange?.([...next]);
  };

  return (
    <div style={{ display: "grid", gap: "8px" }}>
      {items.map((it) => (
        <label key={it.id} style={{ display: "flex", gap: "10px", alignItems: "center", fontSize: "13px" }}>
          <input type="checkbox" checked={set.has(it.id)} onChange={() => toggle(it.id)} />
          <span style={{ color: "var(--text-secondary)" }}>{it.label}</span>
        </label>
      ))}
      <label style={{ display: "flex", gap: "10px", alignItems: "center", fontSize: "13px", marginTop: "6px" }}>
        <input type="checkbox" checked={set.has("OKTA-BASELINE")} onChange={toggleBaseline} />
        <span style={{ color: "var(--text-secondary)" }}>
          {formatComplianceScopeDisplay(["OKTA-BASELINE"])} — default when no other framework is selected
        </span>
      </label>
      <div className="text-dim" style={{ fontSize: "11px", marginTop: "2px" }}>
        If you&apos;re not sure, use Okta Baseline.
      </div>
    </div>
  );
}
